import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  Crown, Compass, Briefcase, Building2, Check, Star,
  Loader2, ArrowLeft, Zap, Gift, Trophy,
} from 'lucide-react';

const TIER_CONFIG = {
  explorer: { icon: Compass, color: 'from-stone-500 to-stone-600', badge: 'bg-stone-100 text-stone-700' },
  navigator: { icon: Star, color: 'from-teal-500 to-teal-700', badge: 'bg-teal-100 text-teal-700' },
  agent: { icon: Briefcase, color: 'from-amber-500 to-amber-700', badge: 'bg-amber-100 text-amber-700' },
  business: { icon: Building2, color: 'from-indigo-500 to-indigo-700', badge: 'bg-indigo-100 text-indigo-700' },
};

const PLAN_FEATURES = {
  explorer: [
    'AI itinerary generation (5/month after trial)',
    'Basic budget calculator',
    'Flight & hotel search links',
    'Community reviews',
    'Full access for first 30 days',
  ],
  navigator: [
    'Unlimited AI itineraries',
    'Priority itinerary generation',
    'Currency converter',
    'PDF itinerary downloads',
    'Photo journal (5GB)',
    'Earn 1 pt per $1 affiliate booking',
  ],
  agent: [
    'Everything in Navigator',
    'White-label itineraries',
    'Bulk generation (50/month)',
    'Client management dashboard',
    'Custom referral link + commissions',
    'Earn 2 pts per $1 client booking',
    'Priority email support',
  ],
  business: [
    'Everything in Agent Partner',
    'API access for integration',
    'Unlimited itineraries',
    'Team accounts (up to 10)',
    'Co-branded marketing materials',
    'Earn 3 pts per $1 referred booking',
    'Dedicated account manager',
  ],
};

const PricingCard = ({ tier, name, monthlyPrice, yearlyPrice, features, isCurrentTier, isPopular, billingCycle, onSubscribe, loading }) => {
  const config = TIER_CONFIG[tier];
  const Icon = config.icon;
  const price = billingCycle === 'yearly' ? yearlyPrice : monthlyPrice;
  const priceLabel = billingCycle === 'yearly' ? '/year' : '/month';
  const isFree = tier === 'explorer';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative bg-white rounded-2xl border-2 p-6 flex flex-col ${isPopular ? 'border-amber-400 shadow-xl shadow-amber-100/50' : 'border-gray-100 shadow-lg'} ${isCurrentTier ? 'ring-2 ring-teal-500' : ''}`}
    >
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-amber-400 to-amber-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md">
          Most Popular
        </div>
      )}
      {isCurrentTier && (
        <div className="absolute -top-3 right-4 bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">
          Current Plan
        </div>
      )}

      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>

      <h3 className="text-xl font-serif font-bold text-gray-900">{name}</h3>

      <div className="mt-3 mb-5">
        {isFree ? (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-gray-900">Free</span>
          </div>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-gray-900">A${price}</span>
            <span className="text-gray-500 text-sm">{priceLabel}</span>
          </div>
        )}
        {billingCycle === 'yearly' && !isFree && (
          <p className="text-xs text-green-600 font-medium mt-1">Save 17% vs monthly</p>
        )}
      </div>

      <ul className="space-y-2.5 flex-1 mb-6">
        {features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {isFree ? (
        <Button disabled className="w-full rounded-full h-11 bg-gray-100 text-gray-500" data-testid="plan-explorer-btn">
          {isCurrentTier ? 'Current Plan' : 'Default'}
        </Button>
      ) : (
        <Button
          onClick={() => onSubscribe(tier, billingCycle)}
          disabled={loading || isCurrentTier}
          className={`w-full rounded-full h-11 font-semibold ${isPopular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white' : 'bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white'}`}
          data-testid={`plan-${tier}-btn`}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isCurrentTier ? 'Current Plan' : 'Get Started'}
        </Button>
      )}
    </motion.div>
  );
};

const PointsSection = () => (
  <div className="mt-16 max-w-4xl mx-auto">
    <div className="text-center mb-8">
      <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Trophy className="w-7 h-7 text-white" />
      </div>
      <h2 className="font-serif text-2xl md:text-3xl text-white drop-shadow-lg mb-2">Triple A Miles</h2>
      <p className="text-white/80">Earn points with every action, redeem for rewards</p>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[
        { action: 'Sign up', points: 100, icon: <Gift className="w-5 h-5" /> },
        { action: 'Complete a trip', points: 50, icon: <Compass className="w-5 h-5" /> },
        { action: 'Submit a review', points: 25, icon: <Star className="w-5 h-5" /> },
        { action: 'Refer a friend', points: 200, icon: <Zap className="w-5 h-5" /> },
        { action: 'Share itinerary', points: 15, icon: <Trophy className="w-5 h-5" /> },
        { action: 'Book via affiliate', points: '1-3 / $1', icon: <Crown className="w-5 h-5" /> },
      ].map((item) => (
        <div key={item.action} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
            {item.icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-800">{item.action}</p>
          </div>
          <span className="text-amber-600 font-bold text-sm whitespace-nowrap">+{item.points} pts</span>
        </div>
      ))}
    </div>
    <div className="mt-8 bg-gradient-to-r from-teal-50 to-amber-50 rounded-2xl p-6 border border-teal-100">
      <h3 className="font-serif text-lg text-gray-900 mb-3">Redeem Your Miles</h3>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div className="flex justify-between"><span className="text-gray-600">1 month Navigator free</span><span className="font-semibold text-teal-700">500 pts</span></div>
        <div className="flex justify-between"><span className="text-gray-600">$10 travel voucher</span><span className="font-semibold text-teal-700">1,000 pts</span></div>
        <div className="flex justify-between"><span className="text-gray-600">AI voice narration itinerary</span><span className="font-semibold text-teal-700">2,500 pts</span></div>
        <div className="flex justify-between"><span className="text-gray-600">1 month Agent Partner free</span><span className="font-semibold text-teal-700">5,000 pts</span></div>
      </div>
    </div>
  </div>
);

const RewardsSection = ({ isAuthenticated }) => {
  const [options, setOptions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [redeeming, setRedeeming] = useState(null);

  const fetchOptions = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/membership/redemption-options');
      if (res.data.success) {
        setOptions(res.data.options);
        setBalance(res.data.balance);
      }
    } catch { /* silent */ }
  }, [isAuthenticated]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  const handleRedeem = async (optionId) => {
    setRedeeming(optionId);
    try {
      const res = await api.post('/membership/redeem', { option_id: optionId });
      if (res.data.success) {
        toast.success(res.data.message);
        setBalance(res.data.new_balance);
        fetchOptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Redemption failed');
    } finally {
      setRedeeming(null);
    }
  };

  if (!isAuthenticated || options.length === 0) return null;

  return (
    <div className="mt-10 max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-1">Redeem Your Miles</h2>
        <p className="text-white/80">Your balance: <span className="font-bold text-gold">{balance} pts</span></p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {options.map((opt) => (
          <div key={opt.id} className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 p-5 flex items-center gap-4 shadow-lg">
            <div className="flex-1">
              <p className="font-semibold text-ocean text-sm">{opt.name}</p>
              <p className="text-xs text-ocean/60">{opt.cost.toLocaleString()} points</p>
            </div>
            <Button
              onClick={() => handleRedeem(opt.id)}
              disabled={!opt.affordable || redeeming === opt.id}
              size="sm"
              className={`rounded-full px-4 text-xs font-semibold ${opt.affordable ? 'bg-gold hover:bg-gold-light text-ocean' : 'bg-gray-200 text-gray-400'}`}
              data-testid={`redeem-${opt.id}`}
            >
              {redeeming === opt.id ? <Loader2 className="w-3 h-3 animate-spin" /> : opt.affordable ? 'Redeem' : 'Not enough'}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function MembershipPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [currentTier, setCurrentTier] = useState('explorer');

  const sessionId = searchParams.get('session_id');

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/membership/status');
      if (res.data.success) setCurrentTier(res.data.tier);
    } catch { /* silent */ }
  }, [isAuthenticated]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Poll payment status if returning from Stripe
  useEffect(() => {
    if (!sessionId || !isAuthenticated) return;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const res = await api.get(`/membership/checkout-status/${sessionId}`);
        if (res.data.payment_status === 'paid') {
          toast.success(`Welcome to ${res.data.tier.charAt(0).toUpperCase() + res.data.tier.slice(1)}! Your membership is active.`);
          setCurrentTier(res.data.tier);
          navigate('/membership', { replace: true });
          return;
        }
      } catch { /* silent */ }
      attempts++;
      if (attempts < maxAttempts) setTimeout(poll, 2500);
      else toast.error('Payment status check timed out. Please refresh the page.');
    };

    toast.info('Verifying your payment...');
    poll();
  }, [sessionId, isAuthenticated, navigate]);

  const handleSubscribe = async (tier, cycle) => {
    if (!isAuthenticated) {
      toast.info('Please sign in first');
      navigate('/auth');
      return;
    }
    setLoading(true);
    try {
      const planId = `${tier}_${cycle === 'yearly' ? 'yearly' : 'monthly'}`;
      const res = await api.post('/membership/subscribe', {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="membership-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>
      <Header />

      <main className="pt-24 pb-20 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="mb-4">
            <button onClick={() => navigate(-1)} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="membership-back-btn">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>

          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Membership Plans</h1>
            <p className="text-white/80 max-w-lg mx-auto">Choose the plan that fits your travel business. Upgrade or downgrade anytime.</p>

            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'monthly' ? 'bg-gold text-ocean shadow-md' : 'bg-white/20 text-white hover:bg-white/30'}`}
                data-testid="billing-monthly"
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${billingCycle === 'yearly' ? 'bg-gold text-ocean shadow-md' : 'bg-white/20 text-white hover:bg-white/30'}`}
                data-testid="billing-yearly"
              >
                Yearly <span className="text-xs opacity-80 ml-1">Save 17%</span>
              </button>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PricingCard
              tier="explorer" name="Explorer" monthlyPrice={0} yearlyPrice={0}
              features={PLAN_FEATURES.explorer} isCurrentTier={currentTier === 'explorer'}
              billingCycle={billingCycle} onSubscribe={handleSubscribe} loading={loading}
            />
            <PricingCard
              tier="navigator" name="Navigator" monthlyPrice={9.99} yearlyPrice={99}
              features={PLAN_FEATURES.navigator} isCurrentTier={currentTier === 'navigator'}
              isPopular billingCycle={billingCycle} onSubscribe={handleSubscribe} loading={loading}
            />
            <PricingCard
              tier="agent" name="Agent Partner" monthlyPrice={29.99} yearlyPrice={299}
              features={PLAN_FEATURES.agent} isCurrentTier={currentTier === 'agent'}
              billingCycle={billingCycle} onSubscribe={handleSubscribe} loading={loading}
            />
            <PricingCard
              tier="business" name="Business Partner" monthlyPrice={99.99} yearlyPrice={999}
              features={PLAN_FEATURES.business} isCurrentTier={currentTier === 'business'}
              billingCycle={billingCycle} onSubscribe={handleSubscribe} loading={loading}
            />
          </div>

          {/* Points & Rewards */}
          <PointsSection />
          <RewardsSection isAuthenticated={isAuthenticated} />
        </div>
      </main>
    </div>
  );
}
